import { AUTH_PATHS } from "@/constants/paths";
import { Pages } from "@/constants/enums";
import useFormFields from "@/hooks/useFormFields";
import useFormValidations from "@/hooks/useFormValidations";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/shared/loader";
import FormFields from "@/components/shared/form-fields/form-fields";
import { useInitiateLogin } from "@/features/auth/store";
import { loginSchema } from "@/validations/login";
import type { z } from "zod";
import { cn } from "@/lib/utils";
import { Form } from "@/components/ui/form";
import { getErrorMessage } from "@/lib/error-handler";
import { AlertCircle } from "lucide-react";
import { resolveAccountEmailAfterLogin } from "@/features/auth/login-utils";

type LoginSchema = z.infer<typeof loginSchema>;

export default function LoginForm() {
  const { getFormFields } = useFormFields({ slug: Pages.LOGIN });
  const { getValidationSchema } = useFormValidations({ slug: Pages.LOGIN });
  const navigate = useNavigate();
  const initiateLogin = useInitiateLogin();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const credentialsForm = useForm<LoginSchema>({
    resolver: zodResolver(getValidationSchema() as typeof loginSchema),
    mode: "onChange",
    defaultValues: {
      identifier: "",
      password: "",
    },
  });

  const {
    handleSubmit: handleCredentialsSubmit,
    control: credentialsControl,
    formState: { errors: credentialsErrors, isSubmitting: isCredentialsSubmitting },
  } = credentialsForm;

  const onCredentialsSubmit = async (data: LoginSchema) => {
    setErrorMessage(null);

    try {
      const response = await initiateLogin(data);

      if (!response.data.requiresOtp) {
        setErrorMessage("تعذر بدء تسجيل الدخول. يرجى المحاولة مرة أخرى.");
        return;
      }

      const accountEmail = resolveAccountEmailAfterLogin(response, data.identifier);
      if (!accountEmail) {
        setErrorMessage("تعذر بدء تسجيل الدخول. يرجى المحاولة مرة أخرى.");
        return;
      }

      const params = new URLSearchParams({ email: accountEmail });

      navigate(`${AUTH_PATHS.ENTER_OTP}?${params.toString()}`, {
        state: {
          message: response.data.message || response.message || null,
        },
      });
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  return (
    <Form {...credentialsForm}>
      <form
        onSubmit={handleCredentialsSubmit(onCredentialsSubmit)}
        className={cn(
          "flex flex-col bg-card p-4 rounded-lg shadow w-full max-w-md",
        )}
      >
        <div className="mb-4 space-y-1 text-center">
          <h2 className="text-lg font-semibold text-foreground">
            تسجيل الدخول
          </h2>
          <p className="text-sm text-muted-foreground">
            أدخل بيانات حسابك للمتابعة
          </p>
        </div>

        {errorMessage && (
          <div className="flex items-start gap-2 mb-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {getFormFields().map((field) => (
          <div key={field.name}>
            <FormFields
              {...field}
              control={credentialsControl}
              errors={credentialsErrors}
            />
          </div>
        ))}

        <Button
          type="submit"
          disabled={isCredentialsSubmitting}
          className={cn("w-full h-10 mt-4 text-white font-medium")}
        >
          <LoadingButton
            loading={isCredentialsSubmitting}
            loadingText="جار إرسال رمز التحقق..."
            loaderSize="sm"
          >
            متابعة
          </LoadingButton>
        </Button>

        <div className="mt-3 text-center">
          <Link
            to={AUTH_PATHS.FORGOT_PASSWORD}
            className="text-sm text-primary hover:underline"
          >
            نسيت كلمة المرور؟
          </Link>
        </div>
      </form>
    </Form>
  );
}
